// @flow

import * as React from 'react';
import { RadioGroup } from '@ta/core-ui.radio-button';
import {
  ReportIAPRadio,
  CommentBox,
  forLabel,
  isOwnerReason,
  type IAPReason,
} from '@ta/iap-reporting.core';
import { SubmitIAPVoteButton } from './submit/SubmitIapVote';
import type { ForumInfo, RepostInfo } from './submit/SubmitIapVote';
import SuggestLocationBox from './new_location_flow/SuggestionLocationBox';
import type { Location } from './new_location_flow/SuggestionLocationBox';
import ReportIAPDefaultReason from './ReportIAPDefaultReason';
import type { ContentIdType } from './content/base';

type Props = {|
  userId: string,
  csrfToken: string,
  contentType: any,
  contentId: ContentIdType,
  children: (panel: any) => React.Node,
  onError: any,
  onComplete: any,
  forumInfo?: ForumInfo,
  repostInfo?: RepostInfo,
|};

// this implementation shares comments/locationids across all radio buttons. This is probably ok?
// the existing one does not but that was an implementation side effect.
type State = {|
  selectedIAPReason: ?IAPReason,
  comment: string,
  suggestedLocation: Location,
  isStateValid: (state: State) => boolean,
  subPanelId: string,
|};

// define the validators up here so we don't keep redefining them in the render method.
// You know what eslint? You're not always right >:(
// eslint-disable-next-line no-unused-vars
export const alwaysNo = (state: State) => false;
// eslint-disable-next-line no-unused-vars
const alwaysYes = (state: State) => true;
const requiredTextValidate = (textLength: number) => (state: State) =>
  state.comment.trim().length > textLength;
const suggestedLocationValidateYes = (state: State) =>
  state.suggestedLocation && state.suggestedLocation.id > 0;
const suggestedLocationValidateNo = requiredTextValidate(10);

/**
 * This class acts as a base for the rest of the report IAP components
 * It provides factory methods to help lay out the widget but makes no assumptions on how you decide to do that
 *
 * It also wires up the state, all you have to do is tell it what components you want.
 *
 * I tried to make the factory methods regular components but they are too intertwined in the state of the panel for that to happen
 *
 * I also considered putting the submit button automatically at the bottom but that proved problematic and/or messy
 * when trying to use Openers. For now, you have to call it manually.
 */

const defaultLocation: Location = { id: 0, label: '' };

class ReportIAPPanel extends React.Component<Props, State> {
  state = {
    selectedIAPReason: undefined,
    comment: '',
    suggestedLocation: defaultLocation,
    // eslint-disable-next-line no-unused-vars
    isStateValid: (state: State) => false,
    subPanelId: '',
  };

  /**
   * This method returns a map of:
   * 1) selectedIAPReason
   * 2) comment
   * 3) suggestedLocationId
   *
   * if the current state is valid for submission for the current reason
   *
   * If it is not, it returns false
   */
  getSubmissionState = () => {
    // no valid state check, return false
    if (!this.state.isStateValid) {
      return false;
    }

    // no selected IAP reason
    if (!this.state.selectedIAPReason || isOwnerReason(this.state.selectedIAPReason)) {
      return false;
    }

    // CMI-4178: Return the reason id, not the label.
    if (this.state.isStateValid(this.state) && this.state.selectedIAPReason) {
      return {
        iapReason: forLabel(this.state.selectedIAPReason).id,
        comment: this.state.comment,
        suggestedLocationId: this.state.suggestedLocation.id,
      };
    }

    return false;
  };

  setNewReason = (reason: IAPReason, validate: (state: State) => boolean) => {
    this.setState({
      selectedIAPReason: reason,
      isStateValid: validate,
      comment: '',
      suggestedLocation: defaultLocation,
    });
  };

  setValidation = (isStateValid: (state: State) => boolean) => {
    this.setState({ isStateValid });
  };

  setSubPanel = (subPanelId: string) => {
    this.setState({ subPanelId });
  };

  setCommentFromCommentBoxEvent = (event: any) => {
    this.setState({
      comment: event.target.value,
    });
  };

  setLocation = (id: number, label: string) => {
    const suggestedLocation = { id, label };
    this.setState({ suggestedLocation });
  };

  resetSubmissionState = () => {
    this.setState({
      comment: '',
      suggestedLocation: defaultLocation,
    });
  };

  createSubmitButton = () => (
    <SubmitIAPVoteButton
      contentId={this.props.contentId}
      contentType={this.props.contentType}
      userId={this.props.userId}
      csrfToken={this.props.csrfToken}
      submissionState={this.getSubmissionState()}
      onComplete={this.props.onComplete}
      onError={this.props.onError}
      onBack={() => {
        this.setSubPanel('');
        this.resetSubmissionState();
      }}
      showBackButton={!!this.state.subPanelId}
      forumInfo={this.props.forumInfo}
      repostInfo={this.props.repostInfo}
    />
  );

  createRadioWithSuggestionBox = (reason: IAPReason, hintText: string) =>
    this.createRadioButtonWithChildren(
      reason,
      alwaysNo,
      <SuggestLocationBox
        hintText={hintText}
        comment={this.state.comment}
        onCommentChange={this.setCommentFromCommentBoxEvent}
        location={this.state.suggestedLocation}
        onLocationChange={this.setLocation}
        onYes={() => {
          this.setValidation(suggestedLocationValidateYes);
          this.resetSubmissionState();
        }}
        onNo={() => {
          this.setValidation(suggestedLocationValidateNo);
          this.resetSubmissionState();
        }}
      />,
    );

  createRadioWithText = (reason: IAPReason, infoText: string) =>
    this.createRadioButtonWithChildren(reason, alwaysYes, infoText);

  createRadio = (reason: IAPReason) => this.createRadioButtonWithChildren(reason, alwaysYes, null);

  createRadioWithOptionalComment = (reason: IAPReason, additionalInfo?: string) =>
    this.createRadioButtonWithChildren(
      reason,
      alwaysYes,
      <>
        {additionalInfo}
        <CommentBox
          value={this.state.comment}
          onChange={this.setCommentFromCommentBoxEvent}
          maxLength={250}
        />
      </>,
    );

  createRadioWithRequiredComment = (reason: IAPReason, additionalInfo?: string) =>
    this.createRadioButtonWithChildren(
      reason,
      requiredTextValidate(10),
      <>
        {additionalInfo}
        <CommentBox
          value={this.state.comment}
          onChange={this.setCommentFromCommentBoxEvent}
          required
          minLength={10}
          maxLength={500}
        />
      </>,
    );

  createRadioWithContentNoSubmit = (reason: IAPReason, content: React.Node) =>
    this.createRadioButtonWithChildren(reason, alwaysNo, content);

  /**
   * Helper method to create radio buttons consistently
   */
  // it has to live in the class because react doesn't like binding the method (even though that's totally valid
  createRadioButtonWithChildren = (
    reason: IAPReason,
    validate: (state: State) => boolean,
    children: any,
  ) => {
    const value = `${reason}`;
    return (
      <ReportIAPRadio
        selected={reason === this.state.selectedIAPReason}
        value={value}
        labelText={forLabel(reason).label}
        onSelect={() => {
          this.setNewReason(reason, validate);
        }}
      >
        {children}
      </ReportIAPRadio>
    );
  };

  createDefaultReasonWithRequiredComment = (reason: IAPReason, minLength: number = 50) =>
    this.createDefaultReasonWithChildren(
      reason,
      requiredTextValidate(minLength),
      <CommentBox
        value={this.state.comment}
        onChange={this.setCommentFromCommentBoxEvent}
        required
        minLength={minLength}
        maxLength={500}
      />,
    );

  createDefaultReasonWithChildren = (
    reason: IAPReason,
    validate: (state: State) => boolean,
    children: any,
  ) => (
    <ReportIAPDefaultReason
      onMountMethod={() => {
        this.setNewReason(reason, validate);
      }}
    >
      {children}
    </ReportIAPDefaultReason>
  );

  createRadioButtonWithSubPanel = (
    reason: IAPReason,
    validate: (state: State) => boolean,
    subPanelId: string,
  ) => {
    const value = `${reason}`;
    return (
      <ReportIAPRadio
        selected={false}
        value={value}
        labelText={forLabel(reason).label}
        onSelect={() => {
          this.setSubPanel(subPanelId);
          this.setNewReason(reason, validate);
        }}
      >
        {null}
      </ReportIAPRadio>
    );
  };

  isSubPanel(subPanelId: string) {
    return this.state.subPanelId === subPanelId;
  }

  render = () => (
    <React.Fragment>
      <RadioGroup name="ReportIAP" labelId="ReportIAP">
        {this.props.children(this)}
      </RadioGroup>

      {this.createSubmitButton()}
    </React.Fragment>
  );
}

export default ReportIAPPanel;
